import React, { useState } from "react";
import { IonIcon, IonModal, useIonRouter } from "@ionic/react";
import {
  bluetooth,
  chevronDown,
  chevronForward,
  pulse,
  chevronBack,
  informationCircleOutline,
} from "ionicons/icons";
import connectedBluetooth from "../../../assets/icons/connectedBluetooth.svg";
import disConnectedBluetooth from "../../../assets/icons/disConnectedBluetooth.svg";
import { AnalyticsIcon } from "../../../assets/icons";
import { useBLE } from "../../../hooks/useBle";
import { useHistory } from "react-router";
import { Capacitor } from "@capacitor/core";
import {
  IOS_CATALOG_NUMBER_BT,
  ANDROID_CATALOG_NUMBER_BT,
  BT_VERSION,
} from "../../../utils/constants";
import "../../../components/Header.css";

interface RemoteHeaderProps {
  isShortHeight: boolean;
  handleFindDevice: () => void;
  customHeader?: string;
  showBtConnectionStatus?: boolean;
  title?: string;
}

const RemoteHeader: React.FC<RemoteHeaderProps> = ({
  isShortHeight,
  handleFindDevice,
  title,
}) => {
  const containerPadding = isShortHeight ? "m-2 -mb-6" : "m-4 mb-2.5";
  const router = useIonRouter();

  const [showVersionAlert, setShowVersionAlert] = useState(false);

  const platform = Capacitor.getPlatform();
  const partNo = platform === "ios" ? IOS_CATALOG_NUMBER_BT : ANDROID_CATALOG_NUMBER_BT;
  const {
    isConnected,
    serialNumber,
    deviceType,
    isConnectClicked,
    parseDeviceString,
    // Add new pulse oximeter states
    isPulseOximeterConnected,
    pulseOximeterDevice,
    connectionStatus,
  } = useBLE();
  const history = useHistory();

  const handleNavigateToAnalytics = () => {
    history.push("/remote/analytics");
  };

  const handleNavigateToCurves = () => {
    history.push("/remote/curves");
  };

  const handleBackButtonClick = () => {
    router.push("/home");
  };

  // Get connection display info
  const getConnectionDisplay = () => {
    const deviceCount = connectionStatus.deviceCount;
    const isAnyConnected = connectionStatus.isConnected;

    return {
      isConnected: isAnyConnected,
      statusText: "Connected",
      statusColor: "#7BD2EC", // Keep existing cyan
      showCount: deviceCount > 1,
      countText: `(${deviceCount})`,
      countColor: "#00FF88", // Green for count
    };
  };

  const connectionDisplay = getConnectionDisplay();

  // Icon button styles for consistent appearance across devices
  const iconButtonStyle = {
    width: "40px",
    height: "40px",
    backgroundColor: "#6B7280",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background-color 0.2s ease-in-out",
  };

  const iconButtonHoverStyle = {
    backgroundColor: "#9CA3AF",
  };

  // Device info container styles
  const deviceInfoContainerStyle = {
    backgroundColor: "#1C1C1E",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background-color 0.2s ease-in-out",
    minWidth: "fit-content",
    maxWidth: "200px",
    marginLeft: "-1px",
    minHeight: "32px",
    borderRadius: "8px",
    padding: "6px 12px",
  };

  const deviceInfoHoverStyle = {
    backgroundColor: "#2C2C2E",
  };

  return (
    <div className={`flex flex-col ${containerPadding}`}>
      {/* Version Info Modal */}
      <IonModal
        isOpen={showVersionAlert}
        mode="ios"
        backdropDismiss
        onDidDismiss={() => setShowVersionAlert(false)}
        style={{
          "--width": "100%",
          "--height": "100%",
          "--background": "transparent",
          "--ion-background-color": "transparent",
          "--backdrop-opacity": "0",
          "--box-shadow": "none",
          "--border-radius": "0",
        }}
      >
        <div className="flex items-center justify-center h-full w-full px-8 bg-black/30 backdrop-blur-[2px]">
          <div className="bg-white rounded-[20px] shadow-xl w-full max-w-[280px] relative overflow-hidden flex flex-col items-center text-center">
            {/* Brand gradient top bar */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[var(--app-gradient-start)] to-[var(--app-gradient-end)]" />

            <div className="pt-8 pb-6 px-6 w-full">
              {/* Icon */}
              <div className="w-12 h-12 rounded-full bg-[#E6F7FB] flex items-center justify-center mx-auto mb-3">
                <IonIcon
                  icon={informationCircleOutline}
                  className="text-3xl text-[var(--ion-color-primary)]"
                />
              </div>

              {/* Title */}
              <h2 className="text-[17px] font-extrabold text-black mb-1" style={{ fontFamily: "Figtree, sans-serif" }}>
                {title ?? "BiWaze® System Remote"}
              </h2>
            
              {/* Info Rows */}
              <div className="w-full flex flex-col gap-2 mb-5">
                {[
                  { label: "Version", value: BT_VERSION },
                  { label: "Catalog Number", value: partNo },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center px-3 py-2.5 rounded-[10px] bg-[#F4F5F8]">
                    <span className="text-[#5b6770] text-[13px]" style={{ fontFamily: "Figtree, sans-serif" }}>{label}</span>
                    <span className="text-black text-[13px] font-semibold" style={{ fontFamily: "Figtree, sans-serif" }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Done Button */}
              <button
                onClick={() => setShowVersionAlert(false)}
                className="w-full py-3 rounded-[35px] font-bold text-white active:scale-95 transition-transform"
                style={{
                  background: "var(--ion-color-primary)",
                  fontFamily: "Figtree, sans-serif",
                  fontSize: "14px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </IonModal>

      <div className="flex justify-between items-center mb-6">
        <div className="back-button" onClick={handleBackButtonClick}>
          <IonIcon
            icon={chevronBack}
            className="md hydrated"
            style={{ fontSize: "24px", color: "rgb(255, 255, 255)" }}
          />
        </div>
        <div className="flex-1 flex justify-center">
          <h1
            className="font-semibold text-white text-xl md:text-3xl select-none text-center"
            onClick={() => setShowVersionAlert(true)}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            {title ?? "BiWaze® System Remote"}
          </h1>
        </div>
        <div className="flex items-center flex-row lg:gap-4 gap-2">
          <div
            style={iconButtonStyle}
            onClick={handleNavigateToCurves}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                iconButtonHoverStyle.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                iconButtonStyle.backgroundColor;
            }}
          >
            <IonIcon
              icon={pulse}
              style={{ fontSize: "20px", color: "#00FFFF" }}
            />
          </div>
          <div
            style={iconButtonStyle}
            onClick={handleNavigateToAnalytics}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                iconButtonHoverStyle.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                iconButtonStyle.backgroundColor;
            }}
          >
            <AnalyticsIcon style={{ width: "20px", height: "20px" }} />
          </div>
        </div>
      </div>

      <div
        className={`mb-${
          isShortHeight ? "3" : "-2"
        }  min-h-[80px] max-h-[127px] flex justify-between items-center gap-4`}
      >
        {/* Left side - Bluetooth icon and enhanced connection status */}
        <div className="flex items-center gap-2">
          <IonIcon
            src={
              connectionDisplay.isConnected
                ? connectedBluetooth
                : disConnectedBluetooth
            }
            style={{ fontSize: "18px" }}
          />
          <div className="flex items-center gap-1">
            <span
              className="text-sm font-normal tracking-[0.374px]"
              style={{
                color: connectionDisplay.isConnected
                  ? connectionDisplay.statusColor
                  : "#FF9F0A",
                fontFamily: "Figtree",
              }}
            >
              {connectionDisplay.isConnected
                ? connectionDisplay.statusText
                : isConnectClicked.status
                ? "Connecting..."
                : "Disconnected"}
            </span>
            {/* Device count indicator */}
            {connectionDisplay.showCount && connectionDisplay.isConnected && (
              <span
                className="text-sm font-normal tracking-[0.374px]"
                style={{
                  color: connectionDisplay.countColor,
                  fontFamily: "Figtree",
                }}
              >
                {connectionDisplay.countText}
              </span>
            )}
          </div>
        </div>

        {/* Right side - Enhanced device info */}
        <div className="flex-shrink-0">
          {(isConnected || isPulseOximeterConnected) &&
          serialNumber.length > 0 ? (
            <div
              style={deviceInfoContainerStyle}
              onClick={handleFindDevice}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  deviceInfoHoverStyle.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  deviceInfoContainerStyle.backgroundColor;
              }}
            >
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  {/* Therapy device info */}
                  {isConnected && serialNumber.length > 0 && (
                    <span
                      className="text-sm leading-tight break-words"
                      style={{
                        fontFamily: "Figtree",
                        fontWeight: 400,
                        letterSpacing: "0.374px",
                        color: "#FFFFFF",
                      }}
                    >
                      {deviceType}
                      <br />
                      {`S/N: ${serialNumber}`}
                    </span>
                  )}

                  {/* Pulse oximeter info */}
                  {isPulseOximeterConnected && pulseOximeterDevice && (
                    <span
                      className="text-xs leading-tight break-words mt-1"
                      style={{
                        fontFamily: "Figtree",
                        fontWeight: 400,
                        letterSpacing: "0.374px",
                        color: "#00FF88", // Green for pulse oximeter
                      }}
                    >
                      {pulseOximeterDevice.device.name || "Pulse Oximeter"}
                    </span>
                  )}
                </div>
                <IonIcon
                  icon={chevronDown}
                  style={{
                    fontSize: "16px",
                    color: "#FFFFFF",
                    flexShrink: 0,
                  }}
                />
              </div>
            </div>
          ) : isConnectClicked.status &&
            isConnectClicked.device?.device.name ? (
            <div
              style={deviceInfoContainerStyle}
              onClick={handleFindDevice}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  deviceInfoHoverStyle.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  deviceInfoContainerStyle.backgroundColor;
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="text-sm leading-tight break-words"
                  style={{
                    fontFamily: "Figtree",
                    fontWeight: 400,
                    letterSpacing: "0.374px",
                    color: "#FFFFFF",
                  }}
                >
                  {`${
                    parseDeviceString(isConnectClicked.device?.device.name)
                      .deviceType
                  } S/N:${
                    parseDeviceString(isConnectClicked.device?.device.name)
                      .serialNumber
                  }`}
                </span>
                <IonIcon
                  icon={chevronDown}
                  style={{
                    fontSize: "16px",
                    color: "#FFFFFF",
                    flexShrink: 0,
                  }}
                />
              </div>
            </div>
          ) : (
            <div
              style={deviceInfoContainerStyle}
              onClick={handleFindDevice}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  deviceInfoHoverStyle.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  deviceInfoContainerStyle.backgroundColor;
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="text-sm"
                  style={{
                    color: "#7BD2EC",
                    fontFamily: "Figtree",
                    letterSpacing: "0.374px",
                    fontWeight: 400,
                  }}
                >
                  Connect to BiWaze®
                </span>
                <IonIcon
                  icon={chevronForward}
                  style={{
                    fontSize: "16px",
                    color: "#FFFFFF",
                    flexShrink: 0,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RemoteHeader;
